
import { Component, Vue, Prop, Watch } from "vue-property-decorator";

import { IRouteMeta } from "@/router/interface";
import program_routes from "@/api/routes/programs";

import { Debounce } from "@/helpers";

import Preloader from "@/components/Preloader.vue";

import ProgramProgram from "@/models/program/program";
import { EProgramVersionStatus, ProgressLevel } from "@/models/program/version";

import CProgramAgreement from "./components/Agreement.vue";
import CProgramAgreementAdmin from "./components/AgreementAdmin.vue";
import CProgramCopy from "./components/Copy.vue";
import CProgramApproval from "./components/Approval.vue";
import CProgramAgreementHistory from "./components/AgreementHistory.vue";
import CProgramDelete from "./components/Delete.vue";
import CProgramSettings from "./components/Settings.vue";
import CProgramRelations from "./components/Relations.vue";

@Component({
  name: "VConstructorEdit",
  components: {
    Preloader,
    CProgramAgreement,
    CProgramAgreementHistory,
    CProgramAgreementAdmin,
    CProgramApproval,
    CProgramCopy,
    CProgramDelete,
    CProgramSettings,
    CProgramRelations
  }
})
export default class VConstructorEdit extends Vue {
  @Prop({ required: true }) public id!: string;

  public preload: boolean = false;
  public disabled: boolean = !this.allowWrite();
  public changes_exist: boolean = false;

  public EProgramVersionStatus = EProgramVersionStatus;

  $refs!: {
    agreement_histories: CProgramAgreementHistory;
    copy: CProgramCopy;
    del: CProgramDelete;
    settings: CProgramSettings;
    agreement_admin: CProgramAgreementAdmin;
  };

  public program: ProgramProgram | null = null;

  public ProgressLevel = ProgressLevel;
  public current_progress_level: number = 1;

  public title: string = "";
  public description: string = "";

  public title_errors: boolean = false;
  public description_errors: boolean = false;

  private update_in_progress: boolean = false;
  public program_edit_message: string = this.$store.state.programEditMessage;

  private current_user: IJWTUser | null = this.$store.state.currentUser;

  private watchers: Function[] = [];

  protected beforeDestroy() {
    this.watchers.forEach(unwatch => {
      unwatch();
    });
  }

  protected async created() {
    this.watchers.push(
      this.$store.watch(
        state => state.programStatusId,
        programStatusId => {
          if (this.program?.draft_version) {
            this.program.draft_version.status_id = programStatusId;
          }

          this.changeDisabled();
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.programChangeExist,
        programChangeExist => {
          this.changes_exist = programChangeExist;
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.programEditMessage,
        programEditMessage => {
          this.program_edit_message = programEditMessage;
        }
      )
    );

    this.watchers.push(
      this.$store.watch(
        state => state.currentUser,
        currentUser => {
          this.current_user = currentUser;
        }
      )
    );

    await this.loadProgram();

    if (this.program) {
      if (this.program.draft_version) {
        this.changes_exist = this.program.draft_version.changes_exist;

        this.$store.commit("programChangeExist", this.changes_exist);
      }

      this.$store.commit("factoryProgramId", this.program.settings.factory_id);
    }
  }

  @Watch("title")
  @Debounce(500)
  public async updateProgramTitle() {
    if (
      this.update_in_progress ||
      !this.program?.draft_version ||
      this.disabled
    ) {
      return;
    }

    this.update_in_progress = true;
    this.title_errors = false;

    return this.$api
      .put(program_routes.version(this.program.draft_version.id), {
        title: this.title
      })
      .then(async () => {
        this.toggleProgramEditMessage();

        await this.changesExist();
      })
      .catch(({ response: res }) => {
        this.title_errors = true;

        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      })
      .finally(() => {
        this.update_in_progress = false;
      });
  }

  @Watch("description")
  @Debounce(500)
  public async updateProgramDescription() {
    if (
      this.update_in_progress ||
      !this.program?.draft_version ||
      this.disabled
    ) {
      return;
    }

    this.update_in_progress = true;
    this.description_errors = false;

    return this.$api
      .put(program_routes.version(this.program.draft_version.id), {
        description: this.description
      })
      .then(async () => {
        this.toggleProgramEditMessage();

        await this.changesExist();
      })
      .catch(({ response: res }) => {
        this.title_errors = true;

        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      })
      .finally(() => {
        this.update_in_progress = false;
      });
  }

  private toggleProgramEditMessage() {
    this.$store.dispatch("toggleProgramEditMessage");
  }

  private async changesExist() {
    if (!this.program?.draft_version?.id) {
      return;
    }

    return this.$api
      .put(program_routes.changes_exist(this.program.draft_version.id), {})
      .then(() => {
        this.$store.commit("programChangeExist", true);
      })
      .catch(({ response: res }) => {
        this.$notify({
          group: "notifications",
          type: "error",
          text: res.data.error,
          duration: 500,
          speed: 500
        });
      });
  }

  public showAgreementHistories() {
    this.$refs.agreement_histories.showHistoryModal();
  }

  public showCopy() {
    this.$refs.copy.showCopyModal();
  }

  public showDelete() {
    this.$refs.del.showDeleteModal();
  }

  public showSettings() {
    this.$refs.settings.showSettingsModal();
  }

  public showAgreementAdmin() {
    this.$refs.agreement_admin.showAgreementModal();
  }

  public hasActualVersion() {
    return (this.program?.draft_version?.version || 1) > 1;
  }

  private async loadProgram() {
    this.preload = true;

    return this.$api
      .get(program_routes.edit(this.id), {
        params: {
          section_id: (this.$router.currentRoute.meta! as IRouteMeta)
            .permissions?.[0]
        }
      })
      .then(({ data: res }: { data: ProgramProgram }) => {
        this.program = res;

        this.title = this.program.draft_version?.title ?? "";
        this.description = this.program.draft_version?.description ?? "";

        this.changeDisabled();
      })
      .finally(() => {
        this.preload = false;
      });
  }

  public changeProgressLevel(level_id: number) {
    this.current_progress_level = level_id;
  }

  private changeDisabled() {
    if (!this.allowWrite()) {
      this.disabled = true;

      return;
    }

    if (this.program!.draft_version) {
      if (
        [
          EProgramVersionStatus.SEND_FOR_APPROVAL,
          EProgramVersionStatus.APPROVED
        ].includes(this.program!.draft_version.status_id)
      ) {
        this.disabled = true;
      }
    }
  }

  public allowWrite() {
    return this.$api.allowWrite();
  }
}
